import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const FORM_ID = "db73oh6n6nb9f68e98vg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db73oh6n6nb9f68e98vg";

type Status = "idle" | "submitting" | "success" | "error";

export default function About() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "关于我们 - 迷人的保姆";
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = String(value).trim();
      if (text !== "") payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交成功，请稍后重试。");
    }
  }

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-6 lg:px-8">
      <nav className="flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑导航">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">关于我们</span>
      </nav>

      <header className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-primary-50 p-5 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-primary-800 md:text-3xl">关于我们</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700">
          迷人的保姆 · 影视剧集资讯站，是一个专注电视剧《迷人的保姆》及同类题材的影迷内容平台。
          我们希望用清晰、详尽的图文内容，帮助每一位观众更好地理解剧情、认识角色，并在追剧之余发现更多好剧。
        </p>
      </header>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 lg:col-span-2">
          <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
            <i className="ri-compass-3-line text-primary-500"></i>
            网站定位
          </h2>
          <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground-700">
            <p>
              我们面向喜欢追剧的影视爱好者，尤其是《迷人的保姆》及相似类型电视剧的粉丝，
              集中提供该剧的全集介绍、剧情解析、角色说明与精选片段回顾。
            </p>
            <p>
              站点内容包括剧集分类浏览、分集剧情介绍、角色背景梳理、精彩片段图文回顾以及相关剧集专题推荐，
              帮助你快速查阅剧集信息、辅助观看与回顾剧情。
            </p>
          </div>

          <h2 className="mt-6 flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
            <i className="ri-copyright-line text-primary-500"></i>
            内容范围与版权声明
          </h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-foreground-700">
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
              本站内容以剧情文字介绍、角色资料与剧照展示为主，仅供影迷交流与学习参考。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
              站内不提供视频播放、本地下载、注册登录及支付功能，所有片段均为图文演示。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
              剧中名称、剧照及图片版权均归原制作方与相关权利人所有，如涉及版权问题请联系我们处理。
            </li>
            <li className="flex gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
              搜索、筛选、分页与收藏等功能均为前端演示实现，收藏记录仅保存在本机浏览器中。
            </li>
          </ul>
        </section>

        <aside className="flex flex-col gap-4 lg:col-span-1">
          <section className="rounded-lg border border-background-200 bg-background-50 p-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
              <i className="ri-contacts-book-line text-primary-500"></i>
              联系我们
            </h2>
            <ul className="mt-3 flex flex-col gap-3 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line text-primary-500"></i>
                反馈邮箱：feedback@charmingnanny.demo
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-wechat-line text-primary-500"></i>
                微信公众号：迷人剧场
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500"></i>
                内容更新时间：每日 09:00 - 22:00
              </li>
            </ul>
          </section>

          <section className="rounded-lg border border-accent-200 bg-accent-50 p-4">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-accent-900">
              <i className="ri-question-answer-line"></i>
              内容纠错
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-accent-900">
              发现剧情介绍有误或信息缺失？欢迎通过右侧反馈表单告诉我们，我们会尽快核对并更新。
            </p>
          </section>
        </aside>
      </div>

      <section className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
        <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-950">
          <i className="ri-feedback-line text-primary-500"></i>
          意见反馈
        </h2>
        <p className="mt-2 text-sm text-foreground-600">
          你的每一条建议都很重要。填写下面的表单，我们会认真阅读并及时回复。
        </p>

        <form
          id={FORM_ID}
          data-readdy-form
          onSubmit={handleSubmit}
          className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          <div className="flex flex-col gap-1.5">
            <label htmlFor="fb-name" className="text-xs font-medium text-foreground-600">
              你的称呼
            </label>
            <input
              id="fb-name"
              name="name"
              type="text"
              required
              placeholder="请输入你的称呼"
              className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="fb-email" className="text-xs font-medium text-foreground-600">
              联系邮箱
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              required
              placeholder="请输入你的邮箱"
              className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="fb-type" className="text-xs font-medium text-foreground-600">
              反馈类型
            </label>
            <select
              id="fb-type"
              name="type"
              defaultValue="内容纠错"
              className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
            >
              <option value="内容纠错">内容纠错</option>
              <option value="剧集推荐">剧集推荐</option>
              <option value="合作建议">合作建议</option>
              <option value="版权问题">版权问题</option>
              <option value="其他反馈">其他反馈</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <label htmlFor="fb-message" className="text-xs font-medium text-foreground-600">
              反馈内容
            </label>
            <textarea
              id="fb-message"
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请描述你的意见或建议（最多 500 字）"
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            ></textarea>
          </div>

          <input
            className="form-extra-field-contact"
            type="text"
            name="contact_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />

          <div className="flex flex-col gap-3 sm:col-span-2 sm:flex-row sm:items-center">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
              {status === "submitting" ? "提交中..." : "提交反馈"}
            </button>
            <p className="text-[11px] text-foreground-400">
              <i className="ri-lock-line"></i> 我们仅将你的信息用于反馈处理，不会用于其他用途。
            </p>
          </div>

          {status === "success" && (
            <p className="flex items-center gap-2 rounded-md bg-primary-50 px-3 py-2.5 text-sm text-primary-700 sm:col-span-2">
              <i className="ri-checkbox-circle-fill"></i>
              反馈提交成功，感谢你的建议，我们会尽快处理！
            </p>
          )}
          {status === "error" && (
            <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900 sm:col-span-2">
              <i className="ri-error-warning-fill"></i>
              {formError || "提交失败，请稍后重试。"}
            </p>
          )}
        </form>
      </section>
    </div>
  );
}